// import { EMAIL_VALIDATE_REGEX } from '@/configs/constants';
import { Button } from '@/components/common/Button';
import FacilityQrSettingsCard from './components/FacilityQrSettingsCard';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/common/Form';
import { Input } from '@/components/common/Input';
import { Switch } from '@/components/common/Switch';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '@/components/common/Select';
import {
    ROUTES,
    SETTING_TIME_AUTO_DELETE_ITEM,
    SETTING_DAILY_DROP_LIMIT,
    SETTING_DROPPABLE_RADIUS_M,
    SETTING_SEARCHABLE_RADIUS_M,
    SETTING_SHAKE_THRESHOLD,
    SETTING_SHAKE_WINDOW_MS,
    SETTING_SHAKE_DEADBAND,
    SETTING_SHAKE_COMPLETION_THRESHOLD,
    SETTING_SHAKE_DURATION_SEC,
    SETTING_SHAKE_BEATS_PER_ACTION,
    SETTING_SHAKE_PREDICT_DURATION_SEC,
    SETTING_VOICE_COMMAND_ENABLED,
    SETTING_SHAKE_EFFECTS_ENABLED,
    SETTING_MAX_ITEMS_PER_USER,
    SETTING_COMMERCIAL_DAILY_LIMIT,
    SETTING_COMMERCIAL_PLACEMENT_RADIUS_M,
    SETTING_OMIKUJI_KYO_BONUS_DURATION_HOURS,
    SETTING_MEO_ADS_ENABLED,
    SETTING_OMIKUJI_LLM_MODEL,
    OMIKUJI_LLM_MODEL_OPTIONS,
    SETTING_COMMERCIAL_RATIO_CAP,
    SETTING_OMIKUJI_KYO_BONUS_MULTIPLIER,
    SETTING_OMIKUJI_FORTUNE_DISTRIBUTION,
    OMIKUJI_FORTUNE_TIERS
} from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { toastify } from '@/utils/toastify';
import { cn } from '@/utils/utils';
import { yupResolver } from '@hookform/resolvers/yup';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

// drop-ux-revamp: フォールバック既定値（UI側の初期表示用）
const DEFAULT_DAILY_DROP_LIMIT = 3;
const DEFAULT_DROPPABLE_RADIUS_M = 100;
// 2026-04-26: 探索範囲のフォールバック (現行 hardcoded ItemService.getNearbyItems の HAVING <=20 と一致)
const DEFAULT_SEARCHABLE_RADIUS_M = 20;
// shake-acquisition: フォールバック既定値（fe-enduser/shakeAccumulator.ts の DEFAULT_* と一致）
const DEFAULT_SHAKE_THRESHOLD = 240;
const DEFAULT_SHAKE_WINDOW_MS = 100;
const DEFAULT_SHAKE_DEADBAND = 6;
// Shake to Spark v2 (2026-05-27): 取得モーダル v2 paradigm 用 default 値 (= backend seed と一致)。
const DEFAULT_SHAKE_COMPLETION_THRESHOLD = 65;
const DEFAULT_SHAKE_DURATION_SEC = 15;
const DEFAULT_SHAKE_BEATS_PER_ACTION = 4;
const DEFAULT_SHAKE_PREDICT_DURATION_SEC = 5;
// 2026-05-27: voice command ON/OFF toggle (backend seed と一致 = true)
const DEFAULT_VOICE_COMMAND_ENABLED = true;
// 2026-09-30: カプセル打ち上げ演出 (第2段) ON/OFF toggle (backend seed と一致 = false)
const DEFAULT_SHAKE_EFFECTS_ENABLED = false;
// 1 ユーザーあたりの累計作成可能アイテム数。backend の DEFAULT_MAX_ITEMS_PER_USER と一致。
const DEFAULT_MAX_ITEMS_PER_USER = 5;
// MEO landmark ads (Phase 1, 2026-05-01): api 側 01_migration_up.sql の seed 値と一致。
const DEFAULT_COMMERCIAL_DAILY_LIMIT = 50;
const DEFAULT_COMMERCIAL_PLACEMENT_RADIUS_M = 5000;
const DEFAULT_OMIKUJI_KYO_BONUS_DURATION_HOURS = 24;
// Step 4-5 続き (2026-05-01): boolean / select keys の default。
const DEFAULT_MEO_ADS_ENABLED = false;
const DEFAULT_OMIKUJI_LLM_MODEL = 'claude-haiku-4-5';
// Step 4-5 続き (2026-05-01): float keys の default (api 側 seed と一致)。
const DEFAULT_COMMERCIAL_RATIO_CAP = 0.4;
const DEFAULT_OMIKUJI_KYO_BONUS_MULTIPLIER = 1.10;
// Step 4-5 完結 (2026-05-01): 占い確率分布 5 階層の default (api 側 seed と一致、合計 100)。
const DEFAULT_FORTUNE_DISTRIBUTION = {
    daikichi: 5,
    chukichi: 20,
    shokichi: 35,
    suekichi: 30,
    kyo: 10
};

// 設定値の妥当範囲。極端値 (threshold=0 等) で本番が壊れるのを防ぐためのガード。
// 上限は各 input の maxLength と整合 (画面上 1 桁多い値が入らないため maxLength と同等の幅)。
const settingSchema = yup.object({
    time: yup
        .number()
        .transform((v, o) => (o === '' ? undefined : v))
        .typeError('数値を入力してください')
        .required('入力が必要です')
        .integer('整数で入力してください')
        .min(1, '1 以上を入力してください')
        .max(999999, '999999 以下を入力してください'),
    dailyDropLimit: yup
        .number()
        .transform((v, o) => (o === '' ? undefined : v))
        .typeError('数値を入力してください')
        .required('入力が必要です')
        .integer('整数で入力してください')
        .min(1, '1 以上を入力してください')
        .max(9999, '9999 以下を入力してください'),
    droppableRadiusM: yup
        .number()
        .transform((v, o) => (o === '' ? undefined : v))
        .typeError('数値を入力してください')
        .required('入力が必要です')
        .integer('整数で入力してください')
        .min(10, '10 以上を入力してください')
        .max(999999, '999999 以下を入力してください'),
    searchableRadiusM: yup
        .number()
        .transform((v, o) => (o === '' ? undefined : v))
        .typeError('数値を入力してください')
        .required('入力が必要です')
        .integer('整数で入力してください')
        .min(10, '10 以上を入力してください')
        .max(999999, '999999 以下を入力してください'),
    maxItemsPerUser: yup
        .number()
        .transform((v, o) => (o === '' ? undefined : v))
        .typeError('数値を入力してください')
        .required('入力が必要です')
        .integer('整数で入力してください')
        .min(1, '1 以上を入力してください')
        .max(9999, '9999 以下を入力してください'),
    shakeThreshold: yup
        .number()
        .transform((v, o) => (o === '' ? undefined : v))
        .typeError('数値を入力してください')
        .required('入力が必要です')
        .integer('整数で入力してください')
        .min(50, '50 以上を入力してください (低すぎると誤検知が増えます)')
        .max(99999, '99999 以下を入力してください'),
    shakeWindowMs: yup
        .number()
        .transform((v, o) => (o === '' ? undefined : v))
        .typeError('数値を入力してください')
        .required('入力が必要です')
        .integer('整数で入力してください')
        .min(50, '50 以上を入力してください')
        .max(99999, '99999 以下を入力してください'),
    shakeDeadband: yup
        .number()
        .transform((v, o) => (o === '' ? undefined : v))
        .typeError('数値を入力してください')
        .required('入力が必要です')
        .integer('整数で入力してください')
        .min(0, '0 以上を入力してください')
        .max(999, '999 以下を入力してください'),
    // Shake to Spark v2 (2026-05-27): 取得モーダル v2 paradigm 用 settings。
    shakeCompletionThreshold: yup
        .number()
        .transform((v, o) => (o === '' ? undefined : v))
        .typeError('数値を入力してください')
        .required('入力が必要です')
        .integer('整数で入力してください')
        .min(10, '10 以上を入力してください (極端に低いと雑振りで取得可能化)')
        .max(100, '100 以下を入力してください'),
    shakeDurationSec: yup
        .number()
        .transform((v, o) => (o === '' ? undefined : v))
        .typeError('数値を入力してください')
        .required('入力が必要です')
        .integer('整数で入力してください')
        .min(5, '5 以上を入力してください')
        .max(60, '60 以下を入力してください'),
    shakeBeatsPerAction: yup
        .number()
        .transform((v, o) => (o === '' ? undefined : v))
        .typeError('数値を入力してください')
        .required('入力が必要です')
        .integer('整数で入力してください')
        .min(1, '1 以上を入力してください')
        .max(8, '8 以下を入力してください'),
    shakePredictDurationSec: yup
        .number()
        .transform((v, o) => (o === '' ? undefined : v))
        .typeError('数値を入力してください')
        .required('入力が必要です')
        .integer('整数で入力してください')
        .min(1, '1 以上を入力してください (server 側でも 1-30 clamp)')
        .max(30, '30 以下を入力してください'),
    commercialDailyLimit: yup
        .number()
        .transform((v, o) => (o === '' ? undefined : v))
        .typeError('数値を入力してください')
        .required('入力が必要です')
        .integer('整数で入力してください')
        .min(1, '1 以上を入力してください')
        .max(99999, '99999 以下を入力してください'),
    commercialPlacementRadiusM: yup
        .number()
        .transform((v, o) => (o === '' ? undefined : v))
        .typeError('数値を入力してください')
        .required('入力が必要です')
        .integer('整数で入力してください')
        .min(10, '10 以上を入力してください')
        .max(999999, '999999 以下を入力してください'),
    omikujiKyoBonusDurationHours: yup
        .number()
        .transform((v, o) => (o === '' ? undefined : v))
        .typeError('数値を入力してください')
        .required('入力が必要です')
        .integer('整数で入力してください')
        .min(1, '1 以上を入力してください')
        .max(720, '720 以下を入力してください (= 30 日)'),
    meoAdsEnabled: yup
        .boolean()
        .required('入力が必要です'),
    voiceCommandEnabled: yup
        .boolean()
        .required('入力が必要です'),
    shakeEffectsEnabled: yup
        .boolean()
        .required('入力が必要です'),
    omikujiLlmModel: yup
        .string()
        .required('選択してください')
        .oneOf(
            OMIKUJI_LLM_MODEL_OPTIONS.map(o => o.value),
            '対応外のモデルです'
        ),
    commercialRatioCap: yup
        .number()
        .transform((v, o) => (o === '' ? undefined : v))
        .typeError('数値を入力してください')
        .required('入力が必要です')
        .min(0, '0 以上を入力してください')
        .max(1, '1 以下を入力してください (= 0.0〜1.0 の比率)'),
    omikujiKyoBonusMultiplier: yup
        .number()
        .transform((v, o) => (o === '' ? undefined : v))
        .typeError('数値を入力してください')
        .required('入力が必要です')
        .min(1.0, '1.0 以上を入力してください (1.0 未満は確率減少を意味し設計外)')
        .max(2.0, '2.0 以下を入力してください (2 倍超のボーナスは過剰)'),
    // Step 4-5 完結: 占い確率分布の 5 階層 (合計 100 縛り)
    fortuneDaikichi: yup
        .number().transform((v, o) => (o === '' ? undefined : v))
        .typeError('数値を入力してください').required('入力が必要です')
        .integer('整数で入力してください').min(0, '0 以上').max(100, '100 以下'),
    fortuneChukichi: yup
        .number().transform((v, o) => (o === '' ? undefined : v))
        .typeError('数値を入力してください').required('入力が必要です')
        .integer('整数で入力してください').min(0, '0 以上').max(100, '100 以下'),
    fortuneShokichi: yup
        .number().transform((v, o) => (o === '' ? undefined : v))
        .typeError('数値を入力してください').required('入力が必要です')
        .integer('整数で入力してください').min(0, '0 以上').max(100, '100 以下'),
    fortuneSuekichi: yup
        .number().transform((v, o) => (o === '' ? undefined : v))
        .typeError('数値を入力してください').required('入力が必要です')
        .integer('整数で入力してください').min(0, '0 以上').max(100, '100 以下'),
    fortuneKyo: yup
        .number().transform((v, o) => (o === '' ? undefined : v))
        .typeError('数値を入力してください').required('入力が必要です')
        .integer('整数で入力してください').min(0, '0 以上').max(100, '100 以下')
}).test('fortune-sum', '占い確率の合計が 100 になるようにしてください', function (values) {
    if (!values) return true;
    const sum = (Number(values.fortuneDaikichi) || 0) +
        (Number(values.fortuneChukichi) || 0) +
        (Number(values.fortuneShokichi) || 0) +
        (Number(values.fortuneSuekichi) || 0) +
        (Number(values.fortuneKyo) || 0);
    if (sum === 100) return true;
    // 末尾の "凶" フィールドにエラーを紐付け、視覚的に近い場所で表示。
    return this.createError({
        path: 'fortuneKyo',
        message: `占い確率の合計が 100 になるようにしてください (現在 ${sum})`
    });
});

// Step 4-5 完結 (2026-05-01): 占い確率分布のリアルタイム合計表示。
// inline IIFE は @stylistic ルール群と相性が悪いので外出し。
const FortuneSumIndicator = ({ form }: { form: any }) => {
    const watched = form.watch();
    const sum = (Number(watched.fortuneDaikichi) || 0) +
        (Number(watched.fortuneChukichi) || 0) +
        (Number(watched.fortuneShokichi) || 0) +
        (Number(watched.fortuneSuekichi) || 0) +
        (Number(watched.fortuneKyo) || 0);
    const isValid = sum === 100;
    const colorCls = isValid ? 'text-green-600' : 'text-orange-600';
    const message = isValid ? `合計: ${sum} ✓` : `合計: ${sum} (100 にしてください)`;
    return (
        <div className={`text-[13px] mt-2 ${colorCls}`}>
            {message}
        </div>
    );
};

export default observer(function SettingPage() {
    // hooks
    const { t } = useTranslation();
    const nav = useNavigate();

    // store
    const {
        settingStore: {
            getTimeAutoDelete,
            updateTimeAutoDelete,
            getDailyDropLimit,
            getDroppableRadiusM,
            getSettingByKey,
            updateSettingByKey,
            clean
        },
        modalStore: { showAlertModal, hideModal }
    } = useStore();

    // lifecycle
    useEffect(() => {
        fetchData();
        return () => {
            clean();
        };
    }, []);

    const [timeDefault, setTimeDefault] = useState(0);
    const [dailyLimitDefault, setDailyLimitDefault] = useState<number>(DEFAULT_DAILY_DROP_LIMIT);
    const [radiusDefault, setRadiusDefault] = useState<number>(DEFAULT_DROPPABLE_RADIUS_M);
    const [searchableRadiusDefault, setSearchableRadiusDefault] = useState<number>(DEFAULT_SEARCHABLE_RADIUS_M);
    const [shakeThresholdDefault, setShakeThresholdDefault] = useState<number>(DEFAULT_SHAKE_THRESHOLD);
    const [shakeWindowDefault, setShakeWindowDefault] = useState<number>(DEFAULT_SHAKE_WINDOW_MS);
    const [shakeDeadbandDefault, setShakeDeadbandDefault] = useState<number>(DEFAULT_SHAKE_DEADBAND);
    // Shake to Spark v2 (2026-05-27): v2 paradigm 用 default state。 fetchData で server 値を読込み、 form.setValue で field に反映。
    const [shakeCompletionThresholdDefault, setShakeCompletionThresholdDefault] = useState<number>(DEFAULT_SHAKE_COMPLETION_THRESHOLD);
    const [shakeDurationSecDefault, setShakeDurationSecDefault] = useState<number>(DEFAULT_SHAKE_DURATION_SEC);
    const [shakeBeatsPerActionDefault, setShakeBeatsPerActionDefault] = useState<number>(DEFAULT_SHAKE_BEATS_PER_ACTION);
    const [shakePredictDurationSecDefault, setShakePredictDurationSecDefault] = useState<number>(DEFAULT_SHAKE_PREDICT_DURATION_SEC);
    // 2026-05-27: voice command ON/OFF (= v2 → v1 fallback switch)
    const [voiceCommandEnabledDefault, setVoiceCommandEnabledDefault] = useState<boolean>(DEFAULT_VOICE_COMMAND_ENABLED);
    // 2026-09-30: カプセル打ち上げ演出 (第2段) ON/OFF
    const [shakeEffectsEnabledDefault, setShakeEffectsEnabledDefault] = useState<boolean>(DEFAULT_SHAKE_EFFECTS_ENABLED);
    const [maxItemsPerUserDefault, setMaxItemsPerUserDefault] = useState<number>(DEFAULT_MAX_ITEMS_PER_USER);
    // MEO 関連 (Step 4-5、integer keys のみ)
    const [commercialDailyLimitDefault, setCommercialDailyLimitDefault] = useState<number>(DEFAULT_COMMERCIAL_DAILY_LIMIT);
    const [commercialPlacementRadiusMDefault, setCommercialPlacementRadiusMDefault] = useState<number>(DEFAULT_COMMERCIAL_PLACEMENT_RADIUS_M);
    const [omikujiKyoBonusDurationHoursDefault, setOmikujiKyoBonusDurationHoursDefault] = useState<number>(DEFAULT_OMIKUJI_KYO_BONUS_DURATION_HOURS);
    // Step 4-5 続き (2026-05-01): boolean / select keys
    const [meoAdsEnabledDefault, setMeoAdsEnabledDefault] = useState<boolean>(DEFAULT_MEO_ADS_ENABLED);
    const [omikujiLlmModelDefault, setOmikujiLlmModelDefault] = useState<string>(DEFAULT_OMIKUJI_LLM_MODEL);
    // Step 4-5 続き (2026-05-01): float keys
    const [commercialRatioCapDefault, setCommercialRatioCapDefault] = useState<number>(DEFAULT_COMMERCIAL_RATIO_CAP);
    const [omikujiKyoBonusMultiplierDefault, setOmikujiKyoBonusMultiplierDefault] = useState<number>(DEFAULT_OMIKUJI_KYO_BONUS_MULTIPLIER);
    // Step 4-5 完結 (2026-05-01): 占い確率分布 (5 階層 合計 100)
    const [fortuneDistributionDefault, setFortuneDistributionDefault] = useState<typeof DEFAULT_FORTUNE_DISTRIBUTION>(DEFAULT_FORTUNE_DISTRIBUTION);

    // state
    const form = useForm({
        resolver: yupResolver(settingSchema),
        mode: 'onSubmit'
    });

    // function
    const fetchData = async () => {
        const [res, dailyRes, radiusRes, searchRadiusRes, shakeThRes, shakeWinRes, shakeDbRes, maxItemsRes, commDailyRes, commRadiusRes, kyoDurationRes, meoEnabledRes, llmModelRes, ratioCapRes, kyoMultRes, fortuneDistRes, shakeCompThRes, shakeDurRes, shakeBeatsRes, shakePredictRes, voiceCommandEnabledRes, shakeEffectsEnabledRes] = await Promise.all([
            flowResult(getTimeAutoDelete()),
            flowResult(getDailyDropLimit()),
            flowResult(getDroppableRadiusM()),
            flowResult(getSettingByKey(SETTING_SEARCHABLE_RADIUS_M)),
            flowResult(getSettingByKey(SETTING_SHAKE_THRESHOLD)),
            flowResult(getSettingByKey(SETTING_SHAKE_WINDOW_MS)),
            flowResult(getSettingByKey(SETTING_SHAKE_DEADBAND)),
            flowResult(getSettingByKey(SETTING_MAX_ITEMS_PER_USER)),
            flowResult(getSettingByKey(SETTING_COMMERCIAL_DAILY_LIMIT)),
            flowResult(getSettingByKey(SETTING_COMMERCIAL_PLACEMENT_RADIUS_M)),
            flowResult(getSettingByKey(SETTING_OMIKUJI_KYO_BONUS_DURATION_HOURS)),
            flowResult(getSettingByKey(SETTING_MEO_ADS_ENABLED)),
            flowResult(getSettingByKey(SETTING_OMIKUJI_LLM_MODEL)),
            flowResult(getSettingByKey(SETTING_COMMERCIAL_RATIO_CAP)),
            flowResult(getSettingByKey(SETTING_OMIKUJI_KYO_BONUS_MULTIPLIER)),
            flowResult(getSettingByKey(SETTING_OMIKUJI_FORTUNE_DISTRIBUTION)),
            // Shake to Spark v2 (2026-05-27)
            flowResult(getSettingByKey(SETTING_SHAKE_COMPLETION_THRESHOLD)),
            flowResult(getSettingByKey(SETTING_SHAKE_DURATION_SEC)),
            flowResult(getSettingByKey(SETTING_SHAKE_BEATS_PER_ACTION)),
            flowResult(getSettingByKey(SETTING_SHAKE_PREDICT_DURATION_SEC)),
            // 2026-05-27: voice command ON/OFF toggle
            flowResult(getSettingByKey(SETTING_VOICE_COMMAND_ENABLED)),
            // 2026-09-30: カプセル打ち上げ演出 (第2段) ON/OFF toggle
            flowResult(getSettingByKey(SETTING_SHAKE_EFFECTS_ENABLED))
        ]);
        if (res) {
            form.setValue('time', res.data.value);
            setTimeDefault(res.data.value);
        }
        // drop-ux-revamp: 存在しないキーは既定値に倒す
        const dailyVal = dailyRes?.data?.value != null ? Number(dailyRes.data.value) : DEFAULT_DAILY_DROP_LIMIT;
        form.setValue('dailyDropLimit', dailyVal);
        setDailyLimitDefault(dailyVal);

        const radiusVal = radiusRes?.data?.value != null ? Number(radiusRes.data.value) : DEFAULT_DROPPABLE_RADIUS_M;
        form.setValue('droppableRadiusM', radiusVal);
        setRadiusDefault(radiusVal);

        // 2026-04-26: 探索範囲、api 側 row が無ければ既定値 (= ItemService の旧 hardcoded 20m) に倒す
        const searchRadiusVal = searchRadiusRes?.data?.value != null ? Number(searchRadiusRes.data.value) : DEFAULT_SEARCHABLE_RADIUS_M;
        form.setValue('searchableRadiusM', searchRadiusVal);
        setSearchableRadiusDefault(searchRadiusVal);

        // shake-acquisition: api 側 row が存在しなければ既定値に倒す
        const shakeThVal = shakeThRes?.data?.value != null ? Number(shakeThRes.data.value) : DEFAULT_SHAKE_THRESHOLD;
        form.setValue('shakeThreshold', shakeThVal);
        setShakeThresholdDefault(shakeThVal);

        const shakeWinVal = shakeWinRes?.data?.value != null ? Number(shakeWinRes.data.value) : DEFAULT_SHAKE_WINDOW_MS;
        form.setValue('shakeWindowMs', shakeWinVal);
        setShakeWindowDefault(shakeWinVal);

        const shakeDbVal = shakeDbRes?.data?.value != null ? Number(shakeDbRes.data.value) : DEFAULT_SHAKE_DEADBAND;
        form.setValue('shakeDeadband', shakeDbVal);
        setShakeDeadbandDefault(shakeDbVal);

        // Shake to Spark v2 (2026-05-27): v2 paradigm settings、 api 側 row が無ければ default に倒す。
        const shakeCompThVal = shakeCompThRes?.data?.value != null ? Number(shakeCompThRes.data.value) : DEFAULT_SHAKE_COMPLETION_THRESHOLD;
        form.setValue('shakeCompletionThreshold', shakeCompThVal);
        setShakeCompletionThresholdDefault(shakeCompThVal);

        const shakeDurVal = shakeDurRes?.data?.value != null ? Number(shakeDurRes.data.value) : DEFAULT_SHAKE_DURATION_SEC;
        form.setValue('shakeDurationSec', shakeDurVal);
        setShakeDurationSecDefault(shakeDurVal);

        const shakeBeatsVal = shakeBeatsRes?.data?.value != null ? Number(shakeBeatsRes.data.value) : DEFAULT_SHAKE_BEATS_PER_ACTION;
        form.setValue('shakeBeatsPerAction', shakeBeatsVal);
        setShakeBeatsPerActionDefault(shakeBeatsVal);

        const shakePredictVal = shakePredictRes?.data?.value != null ? Number(shakePredictRes.data.value) : DEFAULT_SHAKE_PREDICT_DURATION_SEC;
        form.setValue('shakePredictDurationSec', shakePredictVal);
        setShakePredictDurationSecDefault(shakePredictVal);

        // 2026-05-27: voice command ON/OFF toggle、 backend は 'true' / 'false' 文字列で保存。
        const rawVoiceEnabled = voiceCommandEnabledRes?.data?.value;
        const voiceEnabledVal = rawVoiceEnabled === true || rawVoiceEnabled === 'true';
        form.setValue('voiceCommandEnabled', voiceEnabledVal);
        setVoiceCommandEnabledDefault(voiceEnabledVal);

        // 2026-09-30: カプセル打ち上げ演出 (第2段) ON/OFF toggle、 backend は 'true' / 'false' 文字列で保存。
        const rawShakeEffectsEnabled = shakeEffectsEnabledRes?.data?.value;
        const shakeEffectsEnabledVal = rawShakeEffectsEnabled === true || rawShakeEffectsEnabled === 'true';
        form.setValue('shakeEffectsEnabled', shakeEffectsEnabledVal);
        setShakeEffectsEnabledDefault(shakeEffectsEnabledVal);

        const maxItemsVal = maxItemsRes?.data?.value != null ? Number(maxItemsRes.data.value) : DEFAULT_MAX_ITEMS_PER_USER;
        form.setValue('maxItemsPerUser', maxItemsVal);
        setMaxItemsPerUserDefault(maxItemsVal);

        // MEO landmark ads (Phase 1, Step 4-5、integer keys)
        const commDailyVal = commDailyRes?.data?.value != null ? Number(commDailyRes.data.value) : DEFAULT_COMMERCIAL_DAILY_LIMIT;
        form.setValue('commercialDailyLimit', commDailyVal);
        setCommercialDailyLimitDefault(commDailyVal);

        const commRadiusVal = commRadiusRes?.data?.value != null ? Number(commRadiusRes.data.value) : DEFAULT_COMMERCIAL_PLACEMENT_RADIUS_M;
        form.setValue('commercialPlacementRadiusM', commRadiusVal);
        setCommercialPlacementRadiusMDefault(commRadiusVal);

        const kyoDurationVal = kyoDurationRes?.data?.value != null ? Number(kyoDurationRes.data.value) : DEFAULT_OMIKUJI_KYO_BONUS_DURATION_HOURS;
        form.setValue('omikujiKyoBonusDurationHours', kyoDurationVal);
        setOmikujiKyoBonusDurationHoursDefault(kyoDurationVal);

        // boolean: api stores 'true' / 'false' as string (per migration seed)
        const rawEnabled = meoEnabledRes?.data?.value;
        const meoEnabledVal = rawEnabled === true || rawEnabled === 'true';
        form.setValue('meoAdsEnabled', meoEnabledVal);
        setMeoAdsEnabledDefault(meoEnabledVal);

        // string select: 既知のオプション外の値が来た場合は default に倒す (oneOf 通過のため)
        const rawModel = llmModelRes?.data?.value;
        const validModels = OMIKUJI_LLM_MODEL_OPTIONS.map(o => o.value);
        const llmModelVal = typeof rawModel === 'string' && validModels.includes(rawModel) ?
            rawModel :
            DEFAULT_OMIKUJI_LLM_MODEL;
        form.setValue('omikujiLlmModel', llmModelVal);
        setOmikujiLlmModelDefault(llmModelVal);

        // float: api 側は数値をそのまま JSON で返す想定だが、文字列で来る可能性も考慮し parseFloat
        const ratioCapVal = ratioCapRes?.data?.value != null ? parseFloat(String(ratioCapRes.data.value)) : DEFAULT_COMMERCIAL_RATIO_CAP;
        const ratioCapSafe = isFinite(ratioCapVal) ? ratioCapVal : DEFAULT_COMMERCIAL_RATIO_CAP;
        form.setValue('commercialRatioCap', ratioCapSafe);
        setCommercialRatioCapDefault(ratioCapSafe);

        const kyoMultVal = kyoMultRes?.data?.value != null ? parseFloat(String(kyoMultRes.data.value)) : DEFAULT_OMIKUJI_KYO_BONUS_MULTIPLIER;
        const kyoMultSafe = isFinite(kyoMultVal) ? kyoMultVal : DEFAULT_OMIKUJI_KYO_BONUS_MULTIPLIER;
        form.setValue('omikujiKyoBonusMultiplier', kyoMultSafe);
        setOmikujiKyoBonusMultiplierDefault(kyoMultSafe);

        // JSON: api 側は文字列で保存 (migration seed と整合)、object でも tolerantly 解釈
        let fortuneObj = DEFAULT_FORTUNE_DISTRIBUTION;
        const rawFortune = fortuneDistRes?.data?.value;
        try {
            if (typeof rawFortune === 'string') {
                const parsed = JSON.parse(rawFortune);
                if (parsed && typeof parsed === 'object') fortuneObj = { ...DEFAULT_FORTUNE_DISTRIBUTION, ...parsed };
            } else if (rawFortune && typeof rawFortune === 'object') {
                fortuneObj = { ...DEFAULT_FORTUNE_DISTRIBUTION, ...rawFortune };
            }
        } catch (_e) {
            // 不正な JSON が来たら default で埋める
            fortuneObj = DEFAULT_FORTUNE_DISTRIBUTION;
        }
        form.setValue('fortuneDaikichi', Number(fortuneObj.daikichi) || 0);
        form.setValue('fortuneChukichi', Number(fortuneObj.chukichi) || 0);
        form.setValue('fortuneShokichi', Number(fortuneObj.shokichi) || 0);
        form.setValue('fortuneSuekichi', Number(fortuneObj.suekichi) || 0);
        form.setValue('fortuneKyo', Number(fortuneObj.kyo) || 0);
        setFortuneDistributionDefault({
            daikichi: Number(fortuneObj.daikichi) || 0,
            chukichi: Number(fortuneObj.chukichi) || 0,
            shokichi: Number(fortuneObj.shokichi) || 0,
            suekichi: Number(fortuneObj.suekichi) || 0,
            kyo: Number(fortuneObj.kyo) || 0
        });
    };

    const onSearch = async (data?: any) => {
        // 既存: time_auto_delete
        const res = await flowResult(updateTimeAutoDelete({
            key: SETTING_TIME_AUTO_DELETE_ITEM,
            value: data?.time
        }));

        // 占い確率分布は 5 sub-fields → 1 JSON key に圧縮
        const fortuneJson = JSON.stringify({
            daikichi: Number(data?.fortuneDaikichi),
            chukichi: Number(data?.fortuneChukichi),
            shokichi: Number(data?.fortuneShokichi),
            suekichi: Number(data?.fortuneSuekichi),
            kyo: Number(data?.fortuneKyo)
        });

        // drop-ux-revamp + shake-acquisition + searchable + user-item-limit + MEO + v2 paradigm + voice toggle: 20 値を並行更新
        const [dailyRes, radiusRes, searchRadiusRes, shakeThRes, shakeWinRes, shakeDbRes, maxItemsRes, commDailyRes, commRadiusRes, kyoDurationRes, meoEnabledRes, llmModelRes, ratioCapUpdateRes, kyoMultUpdateRes, fortuneDistUpdateRes, shakeCompThUpdateRes, shakeDurUpdateRes, shakeBeatsUpdateRes, shakePredictUpdateRes, voiceCommandEnabledUpdateRes, shakeEffectsEnabledUpdateRes] = await Promise.all([
            flowResult(updateSettingByKey({
                key: SETTING_DAILY_DROP_LIMIT,
                value: Number(data?.dailyDropLimit)
            })),
            flowResult(updateSettingByKey({
                key: SETTING_DROPPABLE_RADIUS_M,
                value: Number(data?.droppableRadiusM)
            })),
            flowResult(updateSettingByKey({
                key: SETTING_SEARCHABLE_RADIUS_M,
                value: Number(data?.searchableRadiusM)
            })),
            flowResult(updateSettingByKey({
                key: SETTING_SHAKE_THRESHOLD,
                value: Number(data?.shakeThreshold)
            })),
            flowResult(updateSettingByKey({
                key: SETTING_SHAKE_WINDOW_MS,
                value: Number(data?.shakeWindowMs)
            })),
            flowResult(updateSettingByKey({
                key: SETTING_SHAKE_DEADBAND,
                value: Number(data?.shakeDeadband)
            })),
            flowResult(updateSettingByKey({
                key: SETTING_MAX_ITEMS_PER_USER,
                value: Number(data?.maxItemsPerUser)
            })),
            flowResult(updateSettingByKey({
                key: SETTING_COMMERCIAL_DAILY_LIMIT,
                value: Number(data?.commercialDailyLimit)
            })),
            flowResult(updateSettingByKey({
                key: SETTING_COMMERCIAL_PLACEMENT_RADIUS_M,
                value: Number(data?.commercialPlacementRadiusM)
            })),
            flowResult(updateSettingByKey({
                key: SETTING_OMIKUJI_KYO_BONUS_DURATION_HOURS,
                value: Number(data?.omikujiKyoBonusDurationHours)
            })),
            flowResult(updateSettingByKey({
                key: SETTING_MEO_ADS_ENABLED,
                // api 側で文字列 'true' / 'false' を期待 (migration seed と整合)
                value: data?.meoAdsEnabled ? 'true' : 'false'
            })),
            flowResult(updateSettingByKey({
                key: SETTING_OMIKUJI_LLM_MODEL,
                value: String(data?.omikujiLlmModel)
            })),
            flowResult(updateSettingByKey({
                key: SETTING_COMMERCIAL_RATIO_CAP,
                value: Number(data?.commercialRatioCap)
            })),
            flowResult(updateSettingByKey({
                key: SETTING_OMIKUJI_KYO_BONUS_MULTIPLIER,
                value: Number(data?.omikujiKyoBonusMultiplier)
            })),
            flowResult(updateSettingByKey({
                key: SETTING_OMIKUJI_FORTUNE_DISTRIBUTION,
                value: fortuneJson
            })),
            // Shake to Spark v2 (2026-05-27)
            flowResult(updateSettingByKey({
                key: SETTING_SHAKE_COMPLETION_THRESHOLD,
                value: Number(data?.shakeCompletionThreshold)
            })),
            flowResult(updateSettingByKey({
                key: SETTING_SHAKE_DURATION_SEC,
                value: Number(data?.shakeDurationSec)
            })),
            flowResult(updateSettingByKey({
                key: SETTING_SHAKE_BEATS_PER_ACTION,
                value: Number(data?.shakeBeatsPerAction)
            })),
            flowResult(updateSettingByKey({
                key: SETTING_SHAKE_PREDICT_DURATION_SEC,
                value: Number(data?.shakePredictDurationSec)
            })),
            // 2026-05-27: voice command ON/OFF toggle、 backend は 'true' / 'false' 文字列で受ける。
            flowResult(updateSettingByKey({
                key: SETTING_VOICE_COMMAND_ENABLED,
                value: data?.voiceCommandEnabled ? 'true' : 'false'
            })),
            // 2026-09-30: カプセル打ち上げ演出 (第2段) ON/OFF toggle、 backend は 'true' / 'false' 文字列で受ける。
            flowResult(updateSettingByKey({
                key: SETTING_SHAKE_EFFECTS_ENABLED,
                value: data?.shakeEffectsEnabled ? 'true' : 'false'
            }))
        ]);

        const allUpdates = [res, dailyRes, radiusRes, searchRadiusRes, shakeThRes, shakeWinRes, shakeDbRes, maxItemsRes, commDailyRes, commRadiusRes, kyoDurationRes, meoEnabledRes, llmModelRes, ratioCapUpdateRes, kyoMultUpdateRes, fortuneDistUpdateRes, shakeCompThUpdateRes, shakeDurUpdateRes, shakeBeatsUpdateRes, shakePredictUpdateRes, voiceCommandEnabledUpdateRes, shakeEffectsEnabledUpdateRes];
        const ok = allUpdates.every(r => r && r.ok);
        if (ok) {
            toastify('success', t('messages.update_time_success'));
            fetchData();
        } else {
            // 並行 update のうち最初に message を持っている res を toast 表示。
            const errMsg = allUpdates.find(r => r?.message)?.message;
            toastify('error', errMsg);
        }
    };

    const handleConfirmChange = () => {
        showAlertModal({
            title: '確認',
            size: 'sm',
            headerClassName: 'border-b border-b-border-7',
            content: t('sentences.confirm_quit_form'),
            onSave: async () => {
            },
            onCancel: async () => {
                form.setValue('time', timeDefault);
                form.setValue('dailyDropLimit', dailyLimitDefault);
                form.setValue('droppableRadiusM', radiusDefault);
                form.setValue('searchableRadiusM', searchableRadiusDefault);
                form.setValue('shakeThreshold', shakeThresholdDefault);
                form.setValue('shakeWindowMs', shakeWindowDefault);
                form.setValue('shakeDeadband', shakeDeadbandDefault);
                form.setValue('maxItemsPerUser', maxItemsPerUserDefault);
                form.setValue('commercialDailyLimit', commercialDailyLimitDefault);
                form.setValue('commercialPlacementRadiusM', commercialPlacementRadiusMDefault);
                form.setValue('omikujiKyoBonusDurationHours', omikujiKyoBonusDurationHoursDefault);
                form.setValue('meoAdsEnabled', meoAdsEnabledDefault);
                form.setValue('voiceCommandEnabled', voiceCommandEnabledDefault);
                form.setValue('shakeEffectsEnabled', shakeEffectsEnabledDefault);
                form.setValue('omikujiLlmModel', omikujiLlmModelDefault);
                form.setValue('commercialRatioCap', commercialRatioCapDefault);
                form.setValue('omikujiKyoBonusMultiplier', omikujiKyoBonusMultiplierDefault);
                form.setValue('fortuneDaikichi', fortuneDistributionDefault.daikichi);
                form.setValue('fortuneChukichi', fortuneDistributionDefault.chukichi);
                form.setValue('fortuneShokichi', fortuneDistributionDefault.shokichi);
                form.setValue('fortuneSuekichi', fortuneDistributionDefault.suekichi);
                form.setValue('fortuneKyo', fortuneDistributionDefault.kyo);
            }
        });
    };

    // drop-ux-revamp: 数値入力の共通 onKeyDown / onChange
    const numericKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (['e', 'E', '+', '-', ',', '.'].includes(e.key)) {
            e.preventDefault();
        }
    };

    // Step 4-5 続き (2026-05-01): float 入力用 (numericKeyDown とほぼ同じだが '.' は許容)
    const floatKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (['e', 'E', '+', '-', ','].includes(e.key)) {
            e.preventDefault();
        }
    };

    // float 用 onChange: 数字 + 1 つの '.' のみ許容
    const sanitizeFloat = (raw: string): string => {
        const cleaned = raw.replace(/[^0-9.]/g, '');
        const firstDot = cleaned.indexOf('.');
        if (firstDot === -1) return cleaned;
        // 2 つ目以降の '.' を削除
        return cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, '');
    };

    return (
        <>
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSearch)}>
                    <div className='layout-card'>
                        <div className='text-black-2 font-medium text-[15px]'>
                            {t('sentences.set_time_delete')}
                        </div>
                        <div className='w-full mt-4'>
                            <FormField
                                control={form.control}
                                name='time'
                                render={({ field }) => (
                                    <FormItem className='flex items-center gap-3'>
                                        <FormLabel className='text-black font-medium text-[15px]'>
                                            {t('words_title.remove_after')}
                                        </FormLabel>

                                        <FormControl>
                                            <div className='relative flex items-center border rounded-md w-[167px]'>
                                                <Input
                                                    className='w-full text-left border-none outline-none appearance-none bg-transparent [&::-webkit-inner-spin-button]:hidden [&::-webkit-outer-spin-button]:hidden'
                                                    type='number'
                                                    inputMode='numeric'
                                                    maxLength={6}
                                                    onKeyDown={numericKeyDown}
                                                    {...field}
                                                    onChange={(e) => {
                                                        let value = e.target.value.replace(/\D/g, '');
                                                        if (value.length > 6) {
                                                            value = value.slice(0, 6);
                                                        }
                                                        field.onChange(value === '' ? '' : Number(value));
                                                    }}
                                                    placeholder='日'
                                                />

                                                <div className='absolute right-2 flex flex-col'>
                                                    <button
                                                        type='button'
                                                        className='text-gray-500 hover:text-black'
                                                        onClick={() => {
                                                            const currentValue = Number(field.value) || 0;
                                                            if (currentValue < 999999) {
                                                                field.onChange(currentValue + 1);
                                                            }
                                                        }}
                                                    >
                                                        <ChevronUp />
                                                    </button>
                                                    <button
                                                        type='button'
                                                        className='text-gray-500 hover:text-black'
                                                        onClick={() => field.onChange(Math.max((field.value || 0) - 1, 0))}
                                                    >
                                                        <ChevronDown />
                                                    </button>
                                                </div>
                                            </div>
                                        </FormControl>
                                        <FormMessage className='ml-2 text-sm' />
                                    </FormItem>
                                )}
                            />
                        </div>
                    </div>

                    {/* drop-ux-revamp: 配置関連設定 */}
                    <div className='layout-card mt-4'>
                        <div className='text-black-2 font-medium text-[15px]'>
                            {t('sentences.drop_settings') || 'アイテムの配置に関する設定'}
                        </div>
                        <div className='w-full mt-4'>
                            <FormField
                                control={form.control}
                                name='dailyDropLimit'
                                render={({ field }) => (
                                    <FormItem className='flex items-center gap-3'>
                                        <FormLabel className='text-black font-medium text-[15px] min-w-[200px]'>
                                            {t('words_title.daily_drop_limit') || '1日あたりの設置上限'}
                                        </FormLabel>
                                        <FormControl>
                                            <div className='relative flex items-center border rounded-md w-[167px]'>
                                                <Input
                                                    className='w-full text-left border-none outline-none appearance-none bg-transparent [&::-webkit-inner-spin-button]:hidden [&::-webkit-outer-spin-button]:hidden'
                                                    type='number'
                                                    inputMode='numeric'
                                                    maxLength={4}
                                                    onKeyDown={numericKeyDown}
                                                    {...field}
                                                    onChange={(e) => {
                                                        let value = e.target.value.replace(/\D/g, '');
                                                        if (value.length > 4) value = value.slice(0, 4);
                                                        field.onChange(value === '' ? '' : Number(value));
                                                    }}
                                                    placeholder='件/日'
                                                />
                                                <div className='absolute right-2 flex flex-col'>
                                                    <button
                                                        type='button'
                                                        className='text-gray-500 hover:text-black'
                                                        onClick={() => {
                                                            const v = Number(field.value) || 0;
                                                            if (v < 9999) field.onChange(v + 1);
                                                        }}
                                                    >
                                                        <ChevronUp />
                                                    </button>
                                                    <button
                                                        type='button'
                                                        className='text-gray-500 hover:text-black'
                                                        onClick={() => field.onChange(Math.max((field.value || 0) - 1, 0))}
                                                    >
                                                        <ChevronDown />
                                                    </button>
                                                </div>
                                            </div>
                                        </FormControl>
                                        <FormMessage className='ml-2 text-sm' />
                                    </FormItem>
                                )}
                            />
                        </div>

                        <div className='w-full mt-4'>
                            <FormField
                                control={form.control}
                                name='droppableRadiusM'
                                render={({ field }) => (
                                    <FormItem className='flex items-center gap-3'>
                                        <FormLabel className='text-black font-medium text-[15px] min-w-[200px]'>
                                            {t('words_title.droppable_radius_m') || '配置可能な半径(m)'}
                                        </FormLabel>
                                        <FormControl>
                                            <div className='relative flex items-center border rounded-md w-[167px]'>
                                                <Input
                                                    className='w-full text-left border-none outline-none appearance-none bg-transparent [&::-webkit-inner-spin-button]:hidden [&::-webkit-outer-spin-button]:hidden'
                                                    type='number'
                                                    inputMode='numeric'
                                                    maxLength={6}
                                                    onKeyDown={numericKeyDown}
                                                    {...field}
                                                    onChange={(e) => {
                                                        let value = e.target.value.replace(/\D/g, '');
                                                        if (value.length > 6) value = value.slice(0, 6);
                                                        field.onChange(value === '' ? '' : Number(value));
                                                    }}
                                                    placeholder='m'
                                                />
                                                <div className='absolute right-2 flex flex-col'>
                                                    <button
                                                        type='button'
                                                        className='text-gray-500 hover:text-black'
                                                        onClick={() => {
                                                            const v = Number(field.value) || 0;
                                                            if (v < 999999) field.onChange(v + 10);
                                                        }}
                                                    >
                                                        <ChevronUp />
                                                    </button>
                                                    <button
                                                        type='button'
                                                        className='text-gray-500 hover:text-black'
                                                        onClick={() => field.onChange(Math.max((field.value || 0) - 10, 0))}
                                                    >
                                                        <ChevronDown />
                                                    </button>
                                                </div>
                                            </div>
                                        </FormControl>
                                        <FormMessage className='ml-2 text-sm' />
                                    </FormItem>
                                )}
                            />
                        </div>

                        {/* 2026-04-26: 探索可能な半径 (エンドユーザが「探索」する範囲)。backend ItemService.getNearbyItems が反映 */}
                        <div className='w-full mt-4'>
                            <FormField
                                control={form.control}
                                name='searchableRadiusM'
                                render={({ field }) => (
                                    <FormItem className='flex items-center gap-3'>
                                        <FormLabel className='text-black font-medium text-[15px] min-w-[200px]'>
                                            {t('words_title.searchable_radius_m') || '探索可能な半径(m)'}
                                        </FormLabel>
                                        <FormControl>
                                            <div className='relative flex items-center border rounded-md w-[167px]'>
                                                <Input
                                                    className='w-full text-left border-none outline-none appearance-none bg-transparent [&::-webkit-inner-spin-button]:hidden [&::-webkit-outer-spin-button]:hidden'
                                                    type='number'
                                                    inputMode='numeric'
                                                    maxLength={6}
                                                    onKeyDown={numericKeyDown}
                                                    {...field}
                                                    onChange={(e) => {
                                                        let value = e.target.value.replace(/\D/g, '');
                                                        if (value.length > 6) value = value.slice(0, 6);
                                                        field.onChange(value === '' ? '' : Number(value));
                                                    }}
                                                    placeholder='m'
                                                />
                                                <div className='absolute right-2 flex flex-col'>
                                                    <button
                                                        type='button'
                                                        className='text-gray-500 hover:text-black'
                                                        onClick={() => {
                                                            const v = Number(field.value) || 0;
                                                            if (v < 999999) field.onChange(v + 10);
                                                        }}
                                                    >
                                                        <ChevronUp />
                                                    </button>
                                                    <button
                                                        type='button'
                                                        className='text-gray-500 hover:text-black'
                                                        onClick={() => field.onChange(Math.max((field.value || 0) - 10, 0))}
                                                    >
                                                        <ChevronDown />
                                                    </button>
                                                </div>
                                            </div>
                                        </FormControl>
                                        <FormMessage className='ml-2 text-sm' />
                                    </FormItem>
                                )}
                            />
                        </div>
                    </div>

                    {/* user-item-limit (2026-04-27): ユーザーが累計で作成できるアイテム数上限。
                        将来課金プラン拡張時は users.plan_type で分岐する形に切替予定。 */}
                    <div className='layout-card mt-4'>
                        <div className='text-black-2 font-medium text-[15px]'>
                            {t('sentences.user_item_limit_settings') || 'アイテム作成に関する設定'}
                        </div>
                        <div className='w-full mt-4'>
                            <FormField
                                control={form.control}
                                name='maxItemsPerUser'
                                render={({ field }) => (
                                    <FormItem className='flex items-center gap-3'>
                                        <FormLabel className='text-black font-medium text-[15px] min-w-[200px]'>
                                            {t('words_title.max_items_per_user') || '1ユーザーあたり作成可能なアイテム数'}
                                        </FormLabel>
                                        <FormControl>
                                            <div className='relative flex items-center border rounded-md w-[167px]'>
                                                <Input
                                                    className='w-full text-left border-none outline-none appearance-none bg-transparent [&::-webkit-inner-spin-button]:hidden [&::-webkit-outer-spin-button]:hidden'
                                                    type='number'
                                                    inputMode='numeric'
                                                    maxLength={4}
                                                    onKeyDown={numericKeyDown}
                                                    {...field}
                                                    onChange={(e) => {
                                                        let value = e.target.value.replace(/\D/g, '');
                                                        if (value.length > 4) value = value.slice(0, 4);
                                                        field.onChange(value === '' ? '' : Number(value));
                                                    }}
                                                    placeholder='件'
                                                />
                                                <div className='absolute right-2 flex flex-col'>
                                                    <button
                                                        type='button'
                                                        className='text-gray-500 hover:text-black'
                                                        onClick={() => {
                                                            const v = Number(field.value) || 0;
                                                            if (v < 9999) field.onChange(v + 1);
                                                        }}
                                                    >
                                                        <ChevronUp />
                                                    </button>
                                                    <button
                                                        type='button'
                                                        className='text-gray-500 hover:text-black'
                                                        onClick={() => field.onChange(Math.max((field.value || 0) - 1, 0))}
                                                    >
                                                        <ChevronDown />
                                                    </button>
                                                </div>
                                            </div>
                                        </FormControl>
                                        <FormMessage className='ml-2 text-sm' />
                                    </FormItem>
                                )}
                            />
                        </div>
                    </div>

                    {/* shake-acquisition: 振り取得チューニング (2026-04-25 追加) */}
                    <div className='layout-card mt-4'>
                        <div className='text-black-2 font-medium text-[15px]'>
                            {t('sentences.shake_settings') || '振り動作によるアイテム取得の判定設定（数値変更は次回ホーム画面再訪時にエンドユーザへ反映されます）'}
                        </div>

                        <div className='w-full mt-4'>
                            <FormField
                                control={form.control}
                                name='shakeThreshold'
                                render={({ field }) => (
                                    <FormItem className='flex items-center gap-3'>
                                        <FormLabel className='text-black font-medium text-[15px] min-w-[200px]'>
                                            {t('words_title.shake_threshold') || '判定値（数値が大きいほど厳しい）'}
                                        </FormLabel>
                                        <FormControl>
                                            <div className='relative flex items-center border rounded-md w-[167px]'>
                                                <Input
                                                    className='w-full text-left border-none outline-none appearance-none bg-transparent [&::-webkit-inner-spin-button]:hidden [&::-webkit-outer-spin-button]:hidden'
                                                    type='number'
                                                    inputMode='numeric'
                                                    maxLength={5}
                                                    onKeyDown={numericKeyDown}
                                                    {...field}
                                                    onChange={(e) => {
                                                        let value = e.target.value.replace(/\D/g, '');
                                                        if (value.length > 5) value = value.slice(0, 5);
                                                        field.onChange(value === '' ? '' : Number(value));
                                                    }}
                                                    placeholder='例: 240'
                                                />
                                                <div className='absolute right-2 flex flex-col'>
                                                    <button
                                                        type='button'
                                                        className='text-gray-500 hover:text-black'
                                                        onClick={() => {
                                                            const v = Number(field.value) || 0;
                                                            if (v < 99999) field.onChange(v + 10);
                                                        }}
                                                    >
                                                        <ChevronUp />
                                                    </button>
                                                    <button
                                                        type='button'
                                                        className='text-gray-500 hover:text-black'
                                                        onClick={() => field.onChange(Math.max((field.value || 0) - 10, 0))}
                                                    >
                                                        <ChevronDown />
                                                    </button>
                                                </div>
                                            </div>
                                        </FormControl>
                                        <FormMessage className='ml-2 text-sm' />
                                    </FormItem>
                                )}
                            />
                        </div>

                        <div className='w-full mt-4'>
                            <FormField
                                control={form.control}
                                name='shakeWindowMs'
                                render={({ field }) => (
                                    <FormItem className='flex items-center gap-3'>
                                        <FormLabel className='text-black font-medium text-[15px] min-w-[200px]'>
                                            {t('words_title.shake_window_ms') || '時間（ミリ秒・短いほど即応）'}
                                        </FormLabel>
                                        <FormControl>
                                            <div className='relative flex items-center border rounded-md w-[167px]'>
                                                <Input
                                                    className='w-full text-left border-none outline-none appearance-none bg-transparent [&::-webkit-inner-spin-button]:hidden [&::-webkit-outer-spin-button]:hidden'
                                                    type='number'
                                                    inputMode='numeric'
                                                    maxLength={5}
                                                    onKeyDown={numericKeyDown}
                                                    {...field}
                                                    onChange={(e) => {
                                                        let value = e.target.value.replace(/\D/g, '');
                                                        if (value.length > 5) value = value.slice(0, 5);
                                                        field.onChange(value === '' ? '' : Number(value));
                                                    }}
                                                    placeholder='例: 100 (ms)'
                                                />
                                                <div className='absolute right-2 flex flex-col'>
                                                    <button
                                                        type='button'
                                                        className='text-gray-500 hover:text-black'
                                                        onClick={() => {
                                                            const v = Number(field.value) || 0;
                                                            if (v < 99999) field.onChange(v + 100);
                                                        }}
                                                    >
                                                        <ChevronUp />
                                                    </button>
                                                    <button
                                                        type='button'
                                                        className='text-gray-500 hover:text-black'
                                                        onClick={() => field.onChange(Math.max((field.value || 0) - 100, 0))}
                                                    >
                                                        <ChevronDown />
                                                    </button>
                                                </div>
                                            </div>
                                        </FormControl>
                                        <FormMessage className='ml-2 text-sm' />
                                    </FormItem>
                                )}
                            />
                        </div>

                        <div className='w-full mt-4'>
                            <FormField
                                control={form.control}
                                name='shakeDeadband'
                                render={({ field }) => (
                                    <FormItem className='flex items-center gap-3'>
                                        <FormLabel className='text-black font-medium text-[15px] min-w-[200px]'>
                                            {t('words_title.shake_deadband') || '振動閾値（数値が大きいほど厳しい）'}
                                        </FormLabel>
                                        <FormControl>
                                            <div className='relative flex items-center border rounded-md w-[167px]'>
                                                <Input
                                                    className='w-full text-left border-none outline-none appearance-none bg-transparent [&::-webkit-inner-spin-button]:hidden [&::-webkit-outer-spin-button]:hidden'
                                                    type='number'
                                                    inputMode='numeric'
                                                    maxLength={3}
                                                    onKeyDown={numericKeyDown}
                                                    {...field}
                                                    onChange={(e) => {
                                                        let value = e.target.value.replace(/\D/g, '');
                                                        if (value.length > 3) value = value.slice(0, 3);
                                                        field.onChange(value === '' ? '' : Number(value));
                                                    }}
                                                    placeholder='例: 6'
                                                />
                                                <div className='absolute right-2 flex flex-col'>
                                                    <button
                                                        type='button'
                                                        className='text-gray-500 hover:text-black'
                                                        onClick={() => {
                                                            const v = Number(field.value) || 0;
                                                            if (v < 999) field.onChange(v + 1);
                                                        }}
                                                    >
                                                        <ChevronUp />
                                                    </button>
                                                    <button
                                                        type='button'
                                                        className='text-gray-500 hover:text-black'
                                                        onClick={() => field.onChange(Math.max((field.value || 0) - 1, 0))}
                                                    >
                                                        <ChevronDown />
                                                    </button>
                                                </div>
                                            </div>
                                        </FormControl>
                                        <FormMessage className='ml-2 text-sm' />
                                    </FormItem>
                                )}
                            />
                        </div>

                        {/* 2026-09-30: カプセル打ち上げ演出 (第2段) 全体 ON/OFF。
                            OFF (default) は今までと完全に同じ動き (0.24秒待機 → ローディング → カード)。
                            不具合時にすぐ戻せるよう、単独のトグルにしている。 */}
                        <div className='w-full mt-4'>
                            <FormField
                                control={form.control}
                                name='shakeEffectsEnabled'
                                render={({ field }) => (
                                    <FormItem className='flex items-center gap-3'>
                                        <FormLabel className='text-black font-medium text-[15px] min-w-[200px]'>
                                            カプセル打ち上げ演出を有効化 (OFF で今までと同じ動き)
                                        </FormLabel>
                                        <FormControl>
                                            <Switch
                                                checked={!!field.value}
                                                onCheckedChange={field.onChange}
                                            />
                                        </FormControl>
                                        <FormMessage className='ml-2 text-sm' />
                                    </FormItem>
                                )}
                            />
                            <div className='text-[12px] text-gray-500 mt-1 ml-2'>
                                ON: 取得の瞬間にカプセルが打ち上がり、パラシュートで降りてきて自動で開く演出 (約1.3秒)。
                                OFF: 従来通り 0.24秒待機 → ローディング → 結果カード。
                            </div>
                        </div>
                    </div>

                    {/* Shake to Spark v2 (2026-05-27): v2 paradigm 用 settings (= 取得モーダル).
                        Spec: design/shake-spark-rhythm-game-v2-spec.md §3.2 / §11.1
                        fe-enduser の AccuracyScorer + ShakeSparkOverlay が dropConfig 経由で読取り適用。 */}
                    <div className='layout-card mt-4'>
                        <div className='text-black-2 font-medium text-[15px]'>
                            Shake to Spark v2 取得モーダル 設定 (= 予告 + リアクション voice paradigm)
                        </div>

                        {/* 2026-05-27: voice command ON/OFF master toggle (= v2 ↔ v1 切替)。
                            OFF にすると enduser は voice 表示・発生を完全 skip し、
                            旧 v1 (= shakeAccumulator + 揺れゲージ) で取得判定する。
                            BGM は OFF でも流す。 取得閾値は上の「振り動作によるアイテム取得の判定設定」 を使う。 */}
                        <div className='w-full mt-4'>
                            <FormField
                                control={form.control}
                                name='voiceCommandEnabled'
                                render={({ field }) => (
                                    <FormItem className='flex items-center gap-3'>
                                        <FormLabel className='text-black font-medium text-[15px] min-w-[200px]'>
                                            ボイスコマンドを有効化 (OFF で v1 揺れゲージにフォールバック)
                                        </FormLabel>
                                        <FormControl>
                                            <Switch
                                                checked={!!field.value}
                                                onCheckedChange={field.onChange}
                                            />
                                        </FormControl>
                                        <FormMessage className='ml-2 text-sm' />
                                    </FormItem>
                                )}
                            />
                            <div className='text-[12px] text-gray-500 mt-1 ml-2'>
                                ON: 予告 + リアクション voice + リズム判定 (= 上の v2 4 設定が効く)。
                                OFF: BGM のみ流す + voice 表示なし、 取得判定は上の「振り動作によるアイテム取得の判定設定」 を使う。
                            </div>
                        </div>

                        {/* completion_threshold: 取得 gate の Groove % */}
                        <div className='w-full mt-4'>
                            <FormField
                                control={form.control}
                                name='shakeCompletionThreshold'
                                render={({ field }) => (
                                    <FormItem className='flex items-center gap-3'>
                                        <FormLabel className='text-black font-medium text-[15px] min-w-[200px]'>
                                            取得閾値 Groove % (= 高いほど厳しい、 default 65)
                                        </FormLabel>
                                        <FormControl>
                                            <Input
                                                className='w-[167px]'
                                                type='number'
                                                inputMode='numeric'
                                                placeholder={`例: ${shakeCompletionThresholdDefault}`}
                                                {...field}
                                                onChange={(e) => {
                                                    const v = e.target.value.replace(/\D/g, '');
                                                    field.onChange(v === '' ? '' : Number(v));
                                                }}
                                            />
                                        </FormControl>
                                        <FormMessage className='ml-2 text-sm' />
                                    </FormItem>
                                )}
                            />
                        </div>

                        {/* duration_sec: active phase の最大秒 */}
                        <div className='w-full mt-4'>
                            <FormField
                                control={form.control}
                                name='shakeDurationSec'
                                render={({ field }) => (
                                    <FormItem className='flex items-center gap-3'>
                                        <FormLabel className='text-black font-medium text-[15px] min-w-[200px]'>
                                            取得モード最大秒 (= active phase、 default 15)
                                        </FormLabel>
                                        <FormControl>
                                            <Input
                                                className='w-[167px]'
                                                type='number'
                                                inputMode='numeric'
                                                placeholder={`例: ${shakeDurationSecDefault}`}
                                                {...field}
                                                onChange={(e) => {
                                                    const v = e.target.value.replace(/\D/g, '');
                                                    field.onChange(v === '' ? '' : Number(v));
                                                }}
                                            />
                                        </FormControl>
                                        <FormMessage className='ml-2 text-sm' />
                                    </FormItem>
                                )}
                            />
                        </div>

                        {/* beats_per_action: 何拍ごとに 1 振り */}
                        <div className='w-full mt-4'>
                            <FormField
                                control={form.control}
                                name='shakeBeatsPerAction'
                                render={({ field }) => (
                                    <FormItem className='flex items-center gap-3'>
                                        <FormLabel className='text-black font-medium text-[15px] min-w-[200px]'>
                                            振り間隔 (BGM 拍数、 default 4 = 1 小節ごと)
                                        </FormLabel>
                                        <FormControl>
                                            <Input
                                                className='w-[167px]'
                                                type='number'
                                                inputMode='numeric'
                                                placeholder={`例: ${shakeBeatsPerActionDefault}`}
                                                {...field}
                                                onChange={(e) => {
                                                    const v = e.target.value.replace(/\D/g, '');
                                                    field.onChange(v === '' ? '' : Number(v));
                                                }}
                                            />
                                        </FormControl>
                                        <FormMessage className='ml-2 text-sm' />
                                    </FormItem>
                                )}
                            />
                        </div>

                        {/* predict_duration_sec: 予告 phase の最大秒 */}
                        <div className='w-full mt-4'>
                            <FormField
                                control={form.control}
                                name='shakePredictDurationSec'
                                render={({ field }) => (
                                    <FormItem className='flex items-center gap-3'>
                                        <FormLabel className='text-black font-medium text-[15px] min-w-[200px]'>
                                            予告 phase 最大秒 (= default 5、 「準備 OK!」 で skip 可)
                                        </FormLabel>
                                        <FormControl>
                                            <Input
                                                className='w-[167px]'
                                                type='number'
                                                inputMode='numeric'
                                                placeholder={`例: ${shakePredictDurationSecDefault}`}
                                                {...field}
                                                onChange={(e) => {
                                                    const v = e.target.value.replace(/\D/g, '');
                                                    field.onChange(v === '' ? '' : Number(v));
                                                }}
                                            />
                                        </FormControl>
                                        <FormMessage className='ml-2 text-sm' />
                                    </FormItem>
                                )}
                            />
                        </div>
                    </div>

                    {/* MEO landmark ads (Phase 1, 2026-05-01, Step 4-5):
                        integer keys (PR #22) + boolean / select keys (本 PR) を管理。
                        残る float (COMMERCIAL_RATIO_CAP / OMIKUJI_KYO_BONUS_MULTIPLIER) と
                        JSON (OMIKUJI_FORTUNE_DISTRIBUTION) は別 PR で追加予定。 */}
                    <div className='layout-card mt-4'>
                        <div className='text-black-2 font-medium text-[15px]'>
                            {t('sentences.meo_settings') || 'MEO ランドマーク広告に関する設定 (Phase 1)'}
                        </div>

                        {/* boolean フラグ: 機能全体の有効化 */}
                        <div className='w-full mt-4'>
                            <FormField
                                control={form.control}
                                name='meoAdsEnabled'
                                render={({ field }) => (
                                    <FormItem className='flex items-center gap-3'>
                                        <FormLabel className='text-black font-medium text-[15px] min-w-[200px]'>
                                            {t('words_title.meo_ads_enabled') || 'MEO 広告モデルを有効化'}
                                        </FormLabel>
                                        <FormControl>
                                            <Switch
                                                checked={!!field.value}
                                                onCheckedChange={field.onChange}
                                            />
                                        </FormControl>
                                        <FormMessage className='ml-2 text-sm' />
                                    </FormItem>
                                )}
                            />
                        </div>

                        <div className='w-full mt-4'>
                            <FormField
                                control={form.control}
                                name='commercialDailyLimit'
                                render={({ field }) => (
                                    <FormItem className='flex items-center gap-3'>
                                        <FormLabel className='text-black font-medium text-[15px] min-w-[200px]'>
                                            {t('words_title.commercial_daily_limit') || '事業者 1 日の設置可能件数'}
                                        </FormLabel>
                                        <FormControl>
                                            <div className='relative flex items-center border rounded-md w-[167px]'>
                                                <Input
                                                    className='w-full text-left border-none outline-none appearance-none bg-transparent [&::-webkit-inner-spin-button]:hidden [&::-webkit-outer-spin-button]:hidden'
                                                    type='number'
                                                    inputMode='numeric'
                                                    maxLength={5}
                                                    onKeyDown={numericKeyDown}
                                                    {...field}
                                                    onChange={(e) => {
                                                        let value = e.target.value.replace(/\D/g, '');
                                                        if (value.length > 5) value = value.slice(0, 5);
                                                        field.onChange(value === '' ? '' : Number(value));
                                                    }}
                                                    placeholder='例: 50 (件/日)'
                                                />
                                                <div className='absolute right-2 flex flex-col'>
                                                    <button
                                                        type='button'
                                                        className='text-gray-500 hover:text-black'
                                                        onClick={() => {
                                                            const v = Number(field.value) || 0;
                                                            if (v < 99999) field.onChange(v + 1);
                                                        }}
                                                    >
                                                        <ChevronUp />
                                                    </button>
                                                    <button
                                                        type='button'
                                                        className='text-gray-500 hover:text-black'
                                                        onClick={() => field.onChange(Math.max((field.value || 0) - 1, 0))}
                                                    >
                                                        <ChevronDown />
                                                    </button>
                                                </div>
                                            </div>
                                        </FormControl>
                                        <FormMessage className='ml-2 text-sm' />
                                    </FormItem>
                                )}
                            />
                        </div>

                        <div className='w-full mt-4'>
                            <FormField
                                control={form.control}
                                name='commercialPlacementRadiusM'
                                render={({ field }) => (
                                    <FormItem className='flex items-center gap-3'>
                                        <FormLabel className='text-black font-medium text-[15px] min-w-[200px]'>
                                            {t('words_title.commercial_placement_radius_m') || '事業者の配置可能半径(m)'}
                                        </FormLabel>
                                        <FormControl>
                                            <div className='relative flex items-center border rounded-md w-[167px]'>
                                                <Input
                                                    className='w-full text-left border-none outline-none appearance-none bg-transparent [&::-webkit-inner-spin-button]:hidden [&::-webkit-outer-spin-button]:hidden'
                                                    type='number'
                                                    inputMode='numeric'
                                                    maxLength={6}
                                                    onKeyDown={numericKeyDown}
                                                    {...field}
                                                    onChange={(e) => {
                                                        let value = e.target.value.replace(/\D/g, '');
                                                        if (value.length > 6) value = value.slice(0, 6);
                                                        field.onChange(value === '' ? '' : Number(value));
                                                    }}
                                                    placeholder='例: 5000 (m)'
                                                />
                                                <div className='absolute right-2 flex flex-col'>
                                                    <button
                                                        type='button'
                                                        className='text-gray-500 hover:text-black'
                                                        onClick={() => {
                                                            const v = Number(field.value) || 0;
                                                            if (v < 999999) field.onChange(v + 100);
                                                        }}
                                                    >
                                                        <ChevronUp />
                                                    </button>
                                                    <button
                                                        type='button'
                                                        className='text-gray-500 hover:text-black'
                                                        onClick={() => field.onChange(Math.max((field.value || 0) - 100, 0))}
                                                    >
                                                        <ChevronDown />
                                                    </button>
                                                </div>
                                            </div>
                                        </FormControl>
                                        <FormMessage className='ml-2 text-sm' />
                                    </FormItem>
                                )}
                            />
                        </div>

                        <div className='w-full mt-4'>
                            <FormField
                                control={form.control}
                                name='omikujiKyoBonusDurationHours'
                                render={({ field }) => (
                                    <FormItem className='flex items-center gap-3'>
                                        <FormLabel className='text-black font-medium text-[15px] min-w-[200px]'>
                                            {t('words_title.omikuji_kyo_bonus_duration_hours') || '凶を結んだ後のボーナス継続時間 (h)'}
                                        </FormLabel>
                                        <FormControl>
                                            <div className='relative flex items-center border rounded-md w-[167px]'>
                                                <Input
                                                    className='w-full text-left border-none outline-none appearance-none bg-transparent [&::-webkit-inner-spin-button]:hidden [&::-webkit-outer-spin-button]:hidden'
                                                    type='number'
                                                    inputMode='numeric'
                                                    maxLength={3}
                                                    onKeyDown={numericKeyDown}
                                                    {...field}
                                                    onChange={(e) => {
                                                        let value = e.target.value.replace(/\D/g, '');
                                                        if (value.length > 3) value = value.slice(0, 3);
                                                        field.onChange(value === '' ? '' : Number(value));
                                                    }}
                                                    placeholder='例: 24 (h)'
                                                />
                                                <div className='absolute right-2 flex flex-col'>
                                                    <button
                                                        type='button'
                                                        className='text-gray-500 hover:text-black'
                                                        onClick={() => {
                                                            const v = Number(field.value) || 0;
                                                            if (v < 720) field.onChange(v + 1);
                                                        }}
                                                    >
                                                        <ChevronUp />
                                                    </button>
                                                    <button
                                                        type='button'
                                                        className='text-gray-500 hover:text-black'
                                                        onClick={() => field.onChange(Math.max((field.value || 0) - 1, 0))}
                                                    >
                                                        <ChevronDown />
                                                    </button>
                                                </div>
                                            </div>
                                        </FormControl>
                                        <FormMessage className='ml-2 text-sm' />
                                    </FormItem>
                                )}
                            />
                        </div>

                        {/* float: 商業アイテム比率上限 (0.0-1.0) */}
                        <div className='w-full mt-4'>
                            <FormField
                                control={form.control}
                                name='commercialRatioCap'
                                render={({ field }) => (
                                    <FormItem className='flex items-center gap-3'>
                                        <FormLabel className='text-black font-medium text-[15px] min-w-[200px]'>
                                            {t('words_title.commercial_ratio_cap') || '周辺アイテム取得時の商業ポストカード比率上限 (0.0〜1.0)'}
                                        </FormLabel>
                                        <FormControl>
                                            <div className='relative flex items-center border rounded-md w-[167px]'>
                                                <Input
                                                    className='w-full text-left border-none outline-none appearance-none bg-transparent [&::-webkit-inner-spin-button]:hidden [&::-webkit-outer-spin-button]:hidden'
                                                    type='text'
                                                    inputMode='decimal'
                                                    onKeyDown={floatKeyDown}
                                                    {...field}
                                                    value={field.value ?? ''}
                                                    onChange={(e) => {
                                                        const sanitized = sanitizeFloat(e.target.value);
                                                        // 末尾が '.' の状態は文字列で保持 (入力途中)
                                                        if (sanitized === '' || sanitized.endsWith('.')) {
                                                            field.onChange(sanitized);
                                                        } else {
                                                            field.onChange(parseFloat(sanitized));
                                                        }
                                                    }}
                                                    placeholder='例: 0.4'
                                                />
                                            </div>
                                        </FormControl>
                                        <FormMessage className='ml-2 text-sm' />
                                    </FormItem>
                                )}
                            />
                        </div>

                        {/* float: 凶ボーナス確率倍率 */}
                        <div className='w-full mt-4'>
                            <FormField
                                control={form.control}
                                name='omikujiKyoBonusMultiplier'
                                render={({ field }) => (
                                    <FormItem className='flex items-center gap-3'>
                                        <FormLabel className='text-black font-medium text-[15px] min-w-[200px]'>
                                            {t('words_title.omikuji_kyo_bonus_multiplier') || '凶ボーナス時の確率倍率 (1.0〜2.0)'}
                                        </FormLabel>
                                        <FormControl>
                                            <div className='relative flex items-center border rounded-md w-[167px]'>
                                                <Input
                                                    className='w-full text-left border-none outline-none appearance-none bg-transparent [&::-webkit-inner-spin-button]:hidden [&::-webkit-outer-spin-button]:hidden'
                                                    type='text'
                                                    inputMode='decimal'
                                                    onKeyDown={floatKeyDown}
                                                    {...field}
                                                    value={field.value ?? ''}
                                                    onChange={(e) => {
                                                        const sanitized = sanitizeFloat(e.target.value);
                                                        if (sanitized === '' || sanitized.endsWith('.')) {
                                                            field.onChange(sanitized);
                                                        } else {
                                                            field.onChange(parseFloat(sanitized));
                                                        }
                                                    }}
                                                    placeholder='例: 1.10'
                                                />
                                            </div>
                                        </FormControl>
                                        <FormMessage className='ml-2 text-sm' />
                                    </FormItem>
                                )}
                            />
                        </div>

                        {/* JSON: 占い結果 5 階層別の出現確率 (合計 100) */}
                        <div className='w-full mt-4'>
                            <div className='text-black font-medium text-[15px] mb-2'>
                                {t('words_title.omikuji_fortune_distribution') || '占い結果別の出現確率 (合計 100、% 表記)'}
                            </div>
                            {/* P1-2 (2026-07-08): 通常排出の出現確率は「tier プリセット管理」へ移行済み。
                                この欄の編集は通常排出には反映されない (= 降格)。 */}
                            <div className='mb-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-[13px] text-amber-800'>
                                ⚠️ 通常の出現確率は「tier プリセット管理」へ移行しました。この欄を編集しても通常の排出には反映されません。出現確率 (weight) の調整は tier プリセット管理で行ってください。
                            </div>
                            <div className='flex flex-wrap gap-3'>
                                {OMIKUJI_FORTUNE_TIERS.map((tier) => {
                                    const fieldName = ('fortune' + tier.key.charAt(0).toUpperCase() + tier.key.slice(1)) as
                                        'fortuneDaikichi' | 'fortuneChukichi' | 'fortuneShokichi' | 'fortuneSuekichi' | 'fortuneKyo';
                                    return (
                                        <FormField
                                            key={tier.key}
                                            control={form.control}
                                            name={fieldName}
                                            render={({ field }) => (
                                                <FormItem className='flex flex-col gap-1 min-w-[110px]'>
                                                    <FormLabel className='text-black font-medium text-[13px]'>
                                                        {tier.label}
                                                    </FormLabel>
                                                    <FormControl>
                                                        <div className='relative flex items-center border rounded-md'>
                                                            <Input
                                                                className='w-full text-left border-none outline-none appearance-none bg-transparent [&::-webkit-inner-spin-button]:hidden [&::-webkit-outer-spin-button]:hidden'
                                                                type='number'
                                                                inputMode='numeric'
                                                                maxLength={3}
                                                                onKeyDown={numericKeyDown}
                                                                {...field}
                                                                onChange={(e) => {
                                                                    let value = e.target.value.replace(/\D/g, '');
                                                                    if (value.length > 3) value = value.slice(0, 3);
                                                                    field.onChange(value === '' ? '' : Number(value));
                                                                }}
                                                                placeholder='0-100'
                                                            />
                                                        </div>
                                                    </FormControl>
                                                    <FormMessage className='text-[12px]' />
                                                </FormItem>
                                            )}
                                        />
                                    );
                                })}
                            </div>
                            {/* リアルタイム合計表示 (yup test とは独立、視覚的補助) */}
                            <FortuneSumIndicator form={form} />
                        </div>

                        {/* string select: 占い文言生成に使う Claude モデル */}
                        <div className='w-full mt-4'>
                            <FormField
                                control={form.control}
                                name='omikujiLlmModel'
                                render={({ field }) => (
                                    <FormItem className='flex items-center gap-3'>
                                        <FormLabel className='text-black font-medium text-[15px] min-w-[200px]'>
                                            {t('words_title.omikuji_llm_model') || '占い文言生成 LLM モデル'}
                                        </FormLabel>
                                        <FormControl>
                                            <Select
                                                onValueChange={field.onChange}
                                                value={typeof field.value === 'string' ? field.value : DEFAULT_OMIKUJI_LLM_MODEL}
                                            >
                                                <SelectTrigger className='w-[300px]'>
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {OMIKUJI_LLM_MODEL_OPTIONS.map(opt => (
                                                        <SelectItem
                                                            key={opt.value}
                                                            value={opt.value}
                                                            className='hover:cursor-pointer'
                                                        >
                                                            {opt.label}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </FormControl>
                                        <FormMessage className='ml-2 text-sm' />
                                    </FormItem>
                                )}
                            />
                        </div>
                    </div>

                    <div className='mt-6 flex justify-center items-center space-x-[10px]'>
                        <Button
                            onClick={() => handleConfirmChange()}
                            type='button'
                            variant='outline'
                            className={cn('w-[166.5px] h-12 bg-background-4 hover:text-text-black-1 border border-border-button text-text-black-1 !rounded-[10px]')}
                        >
                            {t('button.cancel')}
                        </Button>
                        <Button
                            type='submit'
                            variant='active'
                            className={cn('w-[166.5px] h-12 bg-active hover:opacity-80 hover:text-white transition-opacity duration-300 !rounded-[10px]')}
                        >
                            {t('button.save')}
                        </Button>
                    </div>
                </form>
            </Form>
            {/* 宿 QR (2026-09-25, 民泊 PoC Step 1): 本体フォームとは独立に保存する 2 項目 */}
            <FacilityQrSettingsCard />
        </>
    );
});
